import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { detectDeviceLanguage } from '../i18n/locale';
import {
  defaultLanguage,
  type Language,
  supportedLanguages,
  translations,
} from '../i18n/translations';
import { secureStorage } from '../lib/secureStorage';

/**
 * Values substituted into `{{token}}` placeholders. A numeric `count` also
 * selects between the `_one` and `_other` variants of a key.
 */
export type TranslationParams = Record<string, string | number>;

interface TranslationContextValue {
  language: Language;
  setLanguage: (language: Language) => Promise<void>;
  t: (key: string, params?: TranslationParams) => string;
  getTranslation: <T = unknown>(key: string) => T | undefined;
}

const TranslationContext = createContext<TranslationContextValue | undefined>(undefined);
const LANGUAGE_STORAGE_KEY = 'app_language_preference';

const resolvePath = (dictionary: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, dictionary);
};

/**
 * `{{token}}`, the syntax the dictionary already uses in 135 strings and the
 * only one `translation-parity.test.ts` can see, so a placeholder dropped in
 * translation fails a test instead of shipping.
 */
const PLACEHOLDER_PATTERN = /\{\{(\w+)\}\}/g;

type Lookup = (key: string) => unknown;

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;

/**
 * Fills every placeholder, or reports failure. Returns undefined when the
 * template references a token `params` does not carry: a sentence built around
 * a number reads as a finished claim once the number is dropped, and a
 * confidently wrong screen is reported far more slowly than an obviously
 * broken one.
 */
const interpolate = (template: string, params: TranslationParams): string | undefined => {
  let missing: string | undefined;

  const filled = template.replace(PLACEHOLDER_PATTERN, (_placeholder, token: string) => {
    const value = params[token];
    if (value === undefined) {
      missing ??= token;
      return '';
    }
    return String(value);
  });

  if (missing !== undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`Translation is missing the "${missing}" parameter: ${template}`);
    }
    return undefined;
  }

  return filled;
};

/**
 * Prefers `key_one` / `key_other` when a numeric `count` is given, and uses
 * `key` itself otherwise. English, Spanish and Portuguese all split at exactly
 * one, so this is a comparison rather than a plural-rule engine. A key with no
 * variants resolves normally, which is what keeps the existing dictionary
 * working untouched.
 */
const selectTemplate = (
  lookup: Lookup,
  key: string,
  params: TranslationParams | undefined,
): string | undefined => {
  const count = params?.count;

  if (typeof count === 'number') {
    const preferred = count === 1 ? '_one' : '_other';
    const alternate = count === 1 ? '_other' : '_one';
    const variant = asText(lookup(`${key}${preferred}`)) ?? asText(lookup(`${key}${alternate}`));

    if (variant !== undefined) {
      return variant;
    }
  }

  return asText(lookup(key));
};

/**
 * The whole of `t()`, shared so the provider and the out-of-provider fallback
 * cannot drift apart. Every failure degrades the same way `t()` always has:
 * the key comes back, which is visible on screen and searchable in the code.
 */
const translate = (lookup: Lookup, key: string, params?: TranslationParams): string => {
  const template = selectTemplate(lookup, key, params);
  if (template === undefined) {
    return key;
  }

  return interpolate(template, params ?? {}) ?? key;
};

export function TranslationProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage);

  useEffect(() => {
    (async () => {
      try {
        const stored = await secureStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored && supportedLanguages.includes(stored as Language)) {
          setLanguageState(stored as Language);
          return;
        }
      } catch (error) {
        console.warn('Unable to read language preference:', error);
      }
      setLanguageState(detectDeviceLanguage());
    })();
  }, []);

  const getTranslation = useCallback(
    <T,>(key: string): T | undefined => {
      const localized = resolvePath(translations[language], key);
      if (localized !== undefined) {
        return localized as T;
      }

      const fallback = resolvePath(translations[defaultLanguage], key);
      if (fallback !== undefined) {
        return fallback as T;
      }

      return undefined;
    },
    [language],
  );

  const t = useCallback(
    (key: string, params?: TranslationParams) => translate(getTranslation, key, params),
    [getTranslation],
  );

  const setLanguage = useCallback(async (nextLanguage: Language) => {
    if (!supportedLanguages.includes(nextLanguage)) {
      return;
    }

    setLanguageState(nextLanguage);
    try {
      await secureStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch (error) {
      console.warn('Unable to persist language preference:', error);
    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      getTranslation,
    }),
    [language, setLanguage, t, getTranslation],
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

const fallbackGetTranslation = <T = unknown>(key: string): T | undefined => {
  return resolvePath(translations[defaultLanguage], key) as T | undefined;
};

const fallbackT = (key: string, params?: TranslationParams): string =>
  translate(fallbackGetTranslation, key, params);

const fallbackSetLanguage = async () => {
  /* noop fallback */
};

const fallbackContext: TranslationContextValue = {
  language: defaultLanguage,
  setLanguage: fallbackSetLanguage,
  t: fallbackT,
  getTranslation: fallbackGetTranslation,
};

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        'useTranslation called outside TranslationProvider — using default language fallback',
      );
    }
    return fallbackContext;
  }
  return context;
}
